import { formatParams } from '@intigral/utils/lib/helpers';
import { isFreeOperator, isFreeTier } from '@intigral/utils/lib/user';
import { KIDSMODE, playerTypes } from '@libs/constants';
import { useMemo, useRef } from 'react';
import cdnHandler from '../helpers/CDNHandler';
import h, { getCountry, mapOldRatingToNewRating } from '../helpers/common';
import user from '../helpers/user';
import { useConfigurationSections } from './useConfiguration';
import { staticDeviceInfo } from './useCSAI';
import { PlayerAPI } from 'bitmovin-player';
import { getNoAdCtaAdBreakStartTime } from '../features/player/utils/AdsUiUtils';

const useAvod = (playerType: string, isTvod: boolean, asset: any) => {
    const { config } = useConfigurationSections();
    const isAvodEnabled = (config as any)?.avodConfigs?.enableAVOD;
    const isKidsAvodEnabled = (config as any)?.avodConfigs?.enableAVODKidsMode;

    const userInfo = user.getUserInfo();

    const isKidsMode = user.getProfileMode() === KIDSMODE.KID;
    const userOperator = userInfo?.operator?.toUpperCase();

    const isFreeUser = isFreeOperator(userOperator);
    const isFreeTierSub = isFreeTier(userInfo);

    /** Saving current ad number in the ref so that it can be used when setting total ads count value */
    const currentAdNumberRef = useRef(1);

    // Operator with ads enabled according to the operator config in the cms
    const isOperatorWithAdsEnabled = config?.operators?.[userOperator]?.isAdsEnabled ?? false;

    const checkForAds = (asset) => {
        /**
         * Ads is enabled only for VOD for the following cases:
         * 1. Avod flag is enabled (✓)
         * 2. Free user (✓) OR Free tier subscription (✓) AND Operator with ads enabled (✓)
         * 2.1. Or #2 and Kids mode (✓) and kids avod is enabled (✓)
         * Otherwise ads is disabled and will not be shown
         */
        if (!isTvod && playerType === playerTypes.Vod) {
            if (isFreeTierSub && isOperatorWithAdsEnabled) {
                if (isKidsMode && isKidsAvodEnabled) {
                    // on the level of asset (can be an episode or a movie)
                    if (asset?.adEnabled) {
                        return true;
                    }
                } else if (isAvodEnabled && !isKidsMode) {
                    // on the level of asset (can be an episode or a movie)
                    if (asset?.adEnabled) {
                        return true;
                    }
                }
            }
        }
        return false;
    };

    const isAdsEnabled = useMemo(() => {
        /**
         * Ads is enabled only for VOD for the following cases:
         * 1. Avod flag is enabled (✓)
         * 2. Free user (✓) OR Free tier subscription (✓) AND Operator with ads enabled (✓)
         * 2.1. Or #2 and Kids mode (✓) and kids avod is enabled (✓)
         * Otherwise ads is disabled and will not be shown
         */
        if (!isTvod && playerType === playerTypes.Vod) {
            if (isFreeTierSub && isOperatorWithAdsEnabled) {
                if (isKidsMode && isKidsAvodEnabled) {
                    // on the level of asset (can be an episode or a movie)
                    if (asset?.adEnabled) {
                        return true;
                    }
                } else if (isAvodEnabled && !isKidsMode) {
                    // on the level of asset (can be an episode or a movie)
                    if (asset?.adEnabled) {
                        return true;
                    }
                }
            }
        }
        return false;
    }, [isKidsMode, isKidsAvodEnabled, isAvodEnabled, isTvod, playerType, asset]);

    const getAVODLanguage = () => {
        const preferredAudioLanguage = user.getPreferredAudioLanguage().toLowerCase();
        const contentLanguage =
            typeof asset.language === 'string'
                ? [asset.language.toLowerCase()]
                : asset.language.map((lang: string) => lang.toLowerCase());

        if (contentLanguage.includes(preferredAudioLanguage)) {
            return preferredAudioLanguage;
        } else {
            return 'ar';
        }
    };

    /**
     * Converts a duration from milliseconds into a formatted time string.
     *
     * @param ms - Duration in milliseconds.
     * @param forceHours - If true, always returns `HH:mm:ss` even for durations under one hour.
     * @returns Formatted duration as `mm:ss` or `HH:mm:ss`.
     */
    const convertMsToTime = (ms: number, forceHours?: boolean) => {
        // Convert to seconds
        const seconds = Math.floor(ms / 1000);

        // Calculate hours, minutes, seconds
        const hours = Math.floor(seconds / 3600);
        const remainingSecondsAfterHours = seconds % 3600;
        const minutes = Math.floor(remainingSecondsAfterHours / 60);
        const remainingSeconds = remainingSecondsAfterHours % 60;

        const timeParts = [minutes.toString().padStart(2, '0'), remainingSeconds.toString().padStart(2, '0')];

        // Format as HH:mm:ss or mm:ss based on duration
        if (hours > 0 || forceHours) {
            timeParts.unshift(hours.toString().padStart(2, '0'));
        }

        return timeParts.join(':');
    };

    /**
     * Updates the UI elements displaying ads count and timing information.
     *
     * @param currentAdNumber - The current ad number being played. If not provided, uses the stored ref value.
     * @param totalAdsCount - The total number of ads to be displayed.
     * @param totalAdsTime - The total duration of all ads in case of SSAI or duration of ad currently
     * playing in case of CSAI, in milliseconds.
     *
     * @remarks
     * If required elements or parameters are missing, the ads label element (class containing "ui-ads-label") is hidden.
     * The current ad number is capped at the total ads count if it exceeds it.
     *
     * @example
     * updateAdsUI(1, 5, 30000); // Updates UI to show ad 1 of 5, total duration 30 seconds
     */
    const updateAdsUI = (currentAdNumber?: number, totalAdsCount?: number, totalAdsTime?: number) => {
        const currentAdNumberElem = document.querySelector('#currentAdNumber');
        const totalAdsCountElem = document.querySelector('#totalAdsCount');
        const totalAdsTimeElement = document.querySelector('#totalAdsTime');
        const adNumber = currentAdNumber || currentAdNumberRef.current;
        const adsCountElem: HTMLElement | null = document.querySelector('[class*="ui-ads-status"]');

        if (totalAdsTimeElement && totalAdsTime) {
            totalAdsTimeElement.textContent = convertMsToTime(totalAdsTime);
        }

        if (!currentAdNumberElem || !adNumber || !totalAdsCountElem || !totalAdsCount) {
            return;
        } else {
            if (adsCountElem) {
                adsCountElem.style.setProperty('display', 'flex', 'important');
            }
        }

        if (currentAdNumberElem && adNumber) {
            currentAdNumberElem.textContent = (
                totalAdsCount && adNumber > totalAdsCount ? totalAdsCount : adNumber
            ).toString();

            currentAdNumberRef.current = adNumber;
        }

        if (totalAdsCountElem && totalAdsCount) {
            totalAdsCountElem.textContent = totalAdsCount.toString();
        }
    };

    // TODO: only ad duration is needed, no need to pass the whole ads object
    const onAdBeginUI = (playerInstance, ads) => {
        const adProgressBar = playerInstance.getContainer().querySelector('#custom-ad-seekbar-progress');

        const currentTimeSec = playerInstance.getCurrentTime();
        // Mid/post-roll breaks start partway through the stitched timeline, so the in-ad progress and
        // countdown must be measured from the break start — not from absolute player time (which made
        // the bar jump to 100% and the time clamp to 00:00 for mid-rolls). Pre-roll starts at 0, so
        // this is a no-op there.
        const breakStartSec = getNoAdCtaAdBreakStartTime() ?? (ads?.startPosition ?? 0) / 1000;
        const elapsedSec = Math.max(0, currentTimeSec - breakStartSec);
        const adsDurationMs = ads?.duration ?? 0;
        const adsDurationSec = adsDurationMs > 0 ? adsDurationMs / 1000 : 0;
        const remainingMs = adsDurationMs > 0 ? Math.max(0, adsDurationMs - elapsedSec * 1000) : 0;
        const remainingSec = remainingMs / 1000;

        const currentTimeElement = playerInstance.getContainer().querySelector('#currentAdsTime');
        if (currentTimeElement) {
            currentTimeElement.textContent = convertMsToTime(remainingMs);
        }

        const currentTimeInSecondsElement = playerInstance.getContainer().querySelector('#currentAdTimeInSeconds');
        if (currentTimeInSecondsElement) {
            // Whole seconds left, counting down to 0 (ceil so e.g. 0.4s left still shows 1)
            currentTimeInSecondsElement.textContent = String(Math.max(0, Math.ceil(remainingSec)));
        }

        const circleElement = playerInstance.getContainer().querySelector('.circle');
        if (circleElement) {
            circleElement.style.setProperty('opacity', '1');

            const remainingPercentage =
                adsDurationSec > 0 ? Math.min(Math.max((remainingSec / adsDurationSec) * 100, 0), 100) : 0;
            circleElement.style.background = `conic-gradient(#FFDD40 ${remainingPercentage}%, #1d252d 0%)`;
        }

        // Update progress bar based on elapsed time within the ad
        if (adProgressBar) {
            if (adsDurationMs > 0) {
                const progressPercentage = (elapsedSec / adsDurationSec) * 100;
                adProgressBar.style.width = `${Math.min(Math.max(progressPercentage, 0), 100)}%`;
            } else {
                adProgressBar.style.width = '0%'; // No ad or no duration, no progress
            }
        }
    };

    const onAdBreakEndUI = (playerInstance) => {
        const adUiDomElement = playerInstance.getContainer().querySelector('#ads-ui');
        if (adUiDomElement) {
            const adProgressBar = adUiDomElement.querySelector('#custom-ad-seekbar-progress');
            if (adProgressBar) adProgressBar.style.width = '0%';
        }
        resetAdsCounter(playerInstance);
    };

    const resetAdsCounter = (playerInstance: PlayerAPI) => {
        if (!playerInstance) return;

        const adUiDomElement = playerInstance.getContainer().querySelector('#ads-ui');
        const currentAdNumberElem = adUiDomElement?.querySelector('#currentAdNumber');
        if (currentAdNumberElem) {
            currentAdNumberElem.textContent = '0';
        }
    };

    const setupAVODParams = () => {
        if (!config?.avodConfigs) return;
        const cdnUrlParams = config?.avodConfigs?.urlParams;

        const params = {
            ...staticDeviceInfo,
            app_name: config.avodConfigs.adTagParams.appName,
            url: config.avodConfigs.adTagParams.domain,
            ip: h.getUserIP(),
            cb: String(Date.now()),
            did: user.getUserDeviceId(),
            w: window.innerWidth.toString(),
            h: window.innerHeight.toString(),
            country: getCountry(),
            language: getAVODLanguage(),
            content_genre: (asset.genre || []).join(','),
            content_id: asset.guid,
            ifa_type: config.avodConfigs.adTagParams.ifaType,
            coppa: asset?.categoryType?.includes('kids') ? '1' : '0',
            rating: mapOldRatingToNewRating(asset?.ratings?.[0]?.rating),
        };

        cdnHandler.setupAdvertising(params, cdnUrlParams);
    };

    const setupAVODParamsCustomForAsset = (asset) => {
        const adsParams = (config as any).video?.smartlibParams?.adsParams ?? {};
        const cdnUrlParams = config?.avodConfigs?.urlParams;

        const formattedCdnAdsParams = formatParams(adsParams, {
            currentDate: Date.now().toString(),
            deviceId: user.getUserDeviceId(),
            width: window.innerWidth.toString(),
            height: window.innerHeight.toString(),
            country: getCountry(),
            language: getAVODLanguage(),
            contentGenre: (asset.genre || []).join(','),
            isKids: asset?.categoryType?.includes('kids') ? '1' : '0',
            contentId: asset.guid,
        });

        const params = {
            ...formattedCdnAdsParams,
        };

        cdnHandler.setupAdvertising(params, cdnUrlParams);
    };

    return {
        convertMsToTime,
        isAdsEnabled,
        updateAdsUI,
        getAVODLanguage,
        onAdBeginUI,
        onAdBreakEndUI,
        setupAVODParams,
        setupAVODParamsCustomForAsset,
        checkForAds,
        resetAdsCounter,
    };
};

export default useAvod;
